import React from 'react';
import {Button, FormGroup, Label} from "reactstrap";
import ProgressBar from 'react-md-progress-bar';
import HTML from '../HtmlHelpers';
import Utils from "../Utils";
import AutoComplete from '../../components/AutoCompleteInput/AutoCompleteInput';
import Select, {Async as AsyncSelect, components} from 'react-select';
import ToggleInput from './Controls/ToggleInput';
import CheckGroup from './Controls/CheckGroup';
import RadioGroup from './Controls/RadioGroup';
import './FormRenderer.css';
import _ from 'lodash';
import AppVariables from "../AppVariables";
import SessionStateManager from "../SessionStateManager";
import {ResponseStatus} from "../Constants";
import PropTypes from 'prop-types';
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from 'yup';
import PhoneNumberInput from "../../components/PhoneNumberInput/PhoneNumberInput";
import DatePicker from 'react-datepicker';
import moment from 'moment';

const ErrorHint = (props) => {
    return <p className="error-hint">{props.error}</p>
};

//  The error to display for required fields
const RequiredError = 'Field is required';

/**
 * Formatter for values
 */
const Formatters = {

    "number:strict": value => Number(value),

    "number": value => (_.isNil(value) || value === "") ? null : Number(value),

    "string": value => value.toString(),

    "date": value => moment(value).toDate()

};

export const InputFrame = ({ children ,
                            inputStyle,
                            name,
                            icon,
                            title,
                            comment,
                            isBusy,
                            busyOverlay,
                            leftActions ,
                            rightActions ,
                            showLabel = true,
                            showComment = true,
                            errorVisible = true  }) => {

    return <FormGroup key={name} style={inputStyle}>

        <div className="clearfix">

            <div className="pull-left">
                {showLabel && <Label for={name}>{icon && HTML.renderIcon(icon)} {title}</Label>}
                {leftActions}
            </div>

            {rightActions && <div className="pull-right">
                {rightActions}
            </div>}
        </div>
        {showComment && (typeof comment == "function"  ? comment() : comment)}
        {children}
        {errorVisible && <ErrorMessage name={name} className="error-hint mt-1" component={"p"} />}
        {isBusy && <ProgressBar show={true} overlay={busyOverlay}/>}
    </FormGroup>
};

class FormRenderer extends React.Component{

    /**
     * Returns all inputs escaping render inputs
     * @param inputs
     * @returns {Array}
     */
    static getRealInputs(inputs){

        let real = [];

        for(let i = 0;i < inputs.length ; i++){
            const input = inputs[i];
            if(_.isArray(input.group)){
                real =  real.concat( FormRenderer.getRealInputs(input.group) );
            }
            else if(_.isString(input.name)){
                real.push(input);
            }
        }

        return real;
    }

    constructor(props){
        super(props);

        this.state = {
            busyInputs: {},
            statusError: null,
            inputs: props.inputs,
            initialValues: props.initialValues || {},
            validationSchema: null,
            handleInputsChanged: (inputs) => {

                 return {
                     validationSchema: this.generateValidationSchema(inputs)
                 };

            }
        };

        //  holds reference to inputs
        this._isMounted = false;
        this.getFormContext = this.getFormContext.bind(this);
    }

    setStateSafe(state,callback){
        
        if(this._isMounted){
            this.setState(state,callback);
        }
        
    }
    
    /**
     * Checks whether the specified input has any error associated
     * @param key
     * @returns {*}
     */
    inputHasError(key){
        return this.getFormContext().errors.hasOwnProperty(key);
    }

    getAPIInput(name){
        return FormRenderer.getRealInputs(this.props.inputs).find(x => (x.hasOwnProperty('api-name') ? x['api-name'] : x.name) === name );
    }

    /**
     * Indicates form is valid
     */
    isValid(){
        return this.getFormContext().isValid;
    }

    componentDidMount(){

        this._isMounted = true;
        const props = this.props;
        if(props.sessionKey){
            this.session = new SessionStateManager(props.sessionKey , true );
            const formData =  this.session.getState('_formData',{});
            this.bindItem(formData);
        }

    }

    __internalSaveState(){

        try {
            const formData = this.getFormData();
            this.session.putState('_formData',formData ).save();
        }
        catch (ex){
            console.error('FormRenderer','Failed to save form renderer state');
        }

    }

    saveState(){

        if(this.session){
            this.__internalSaveState();
        }

    }

    clearState(){

        if(this.session){
            this.session.reset();
        }
    }

    componentWillUnmount(){

        this._isMounted = false;
        
        if(this.session){
            this.__internalSaveState();
        }
    }


    setStatusError(error){

        this.setStateSafe({
            statusError: error
        });

    }

    /**
     * Process error API Response
     * @param err
     * @param showStatusError
     */
    updateException(err , showStatusError = false){

        const response = err.response;
        let statusError;

        //  BAD_REQUEST ??
        if(response && response.body && response.status === 400){

            const body = response.body;
            if(body.status === ResponseStatus.ValidationError && body.error.detail.length > 0){

                const detail = body.error.detail;

                //
                let errorPayload = { };

                //  find associated fields and flag them
                detail.forEach(f => {

                    const field = f.field;
                    let input = this.getAPIInput(field);
                    if(input){
                        errorPayload[input.name] = f.message;
                    }

                });

                //  apply errors to form
                this.getFormContext().setErrors(errorPayload);

                if(detail.length === 1) {
                    statusError = detail[0].message;
                }
                else{
                    statusError = 'Failed: Some fields were invalid. Please fix and try again.';
                }

            }

        }

        if(!statusError){
            statusError = Utils.getResponseError(err);
        }

        if(showStatusError) {

            //  set status error
            this.setStatusError(statusError);

        }
    }

    renderInput(item,context){

        const me = this;
        const props = this.props;
        const isBusy = props.busy;
        const { errors , setFieldValue , touched } = context;
        const inputStyle = props.inputStyle || item.style;
        const { name , type, title , comment , icon , required , refresh , hint , items , setValue , getValue, defaultValue  ,...inputProps} = item;
        const inputError = errors.hasOwnProperty(name);
        const className =  "form-control form-input-select " + ((inputError && touched[name]) ? "form-input-error" : "");
        const busyInput = this.isInputBusy(name);

        switch (type) {

            case "datetime": {

                return <InputFrame name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}>

                    <Field name={name}>
                        {({ field }) => {

                            return <DatePicker  {...inputProps}
                                                {...field}
                                                className={className}
                                                disabled={isBusy}
                                                selected={field.value ? moment(field.value).toDate() : field.value}
                                                placeholderText={hint}
                                                dropdownMode={"scroll"}
                                                onChange={value => setFieldValue(field.name,value)}/>
                        }}
                    </Field>
                </InputFrame>;

            }break;

            case "select": {

                let options = (typeof items === "function" ? items() : items);
                const hasRefresh = typeof refresh == "function";
                return <InputFrame name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}
                                   rightActions={hasRefresh ? <Button color="link" style={{padding: 0, fontSize: 'small'}} onClick={refresh.bind(this,item)}><i className="fa fa-refresh"></i> Refresh</Button>: null}>

                    <Field name={name}
                           component={"select"}
                           className={className}
                           style={{height: '38px'}}
                           disabled={isBusy}
                           {...inputProps}
                           onChange={evt => {

                               const value = evt.target.value;
                               if(_.isFunction(item.onChange)){
                                   item.onChange(value);
                               }

                               setFieldValue(name,value);

                           }}>
                        {options && options.map((opt, k) => (<option value={opt.value} key={k} disabled={opt.disabled} selected={opt.selected}>{opt.text}</option>))}
                    </Field>
                </InputFrame>;
            }
            case 'dropdown': {

                let options  = (typeof items === "function" ? items() : items);
                let {
                    simple,
                    autoFocus,
                    openOnFocus,
                    openOnClick,
                    searchable,
                    multi,
                    async,
                    singleItemRender,
                    dropdownItemRender,
                    ...selectProps } = inputProps;

                return <InputFrame name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}>
                        <Field name={name}
                               {...selectProps}>
                            {({ field , form: { setFieldValue , setFieldTouched } }) => {

                                let value = simple ?
                                    (field.value ? field.value.split(",").map(v => ({ value: v , label: v  })): (multi ? [] : '') ):
                                    (multi ? field.value.map(v => ({ value: v, label: item.getItemLabel(v) , instance: v })) : field.value);

                                const Component = async ? AsyncSelect : Select;
                                let selectComponents = {};
                                if(singleItemRender){

                                    selectComponents['SingleValue'] = ({ children ,...props }) => <components.SingleValue {...props}>
                                        {singleItemRender(props)}
                                    </components.SingleValue>;

                                }

                                if(dropdownItemRender){
                                    selectComponents['Option'] = ({ children , ...props }) => <components.Option {...props}>
                                        {dropdownItemRender(props)}
                                    </components.Option>;
                                }

                                return <Component
                                    {...field}
                                    value={value}
                                    onBlurResetsInput={false}
                                    onSelectResetsInput={false}
                                    simpleValue={simple}
                                    placeholder={hint}
                                    disabled={isBusy}
                                    isMulti={multi}
                                    options={options.filter(c => {

                                        if(!c.hasOwnProperty('label'))
                                            c.label = c.text;

                                        return true;
                                    })}
                                    autoFocus={autoFocus === undefined ? false : autoFocus}
                                    openOnFocus={openOnFocus === undefined ? openOnFocus : false}
                                    openOnClick={openOnClick === undefined ? openOnClick : true }
                                    searchable={searchable === undefined ? searchable: true }
                                    {...selectProps}
                                    onChange={(value) => {

                                        let _actualValue;
                                        if(simple){
                                            _actualValue = multi ? value.map(c => c.value).join(",") : value.value;
                                        }
                                        else{
                                            _actualValue = multi ? value.map(c => c.value)  : value;
                                        }

                                        //
                                        if(item.onChange)
                                            item.onChange(_actualValue);

                                        setFieldValue(name,_actualValue);
                                    }}
                                    onBlur={evt => {

                                    setFieldTouched(name,true);

                                    if(item.onBlur)
                                        item.onBlur(evt);
                                }}/>

                            }}
                        </Field>
                    </InputFrame>;
            }
            case 'location':

                return <InputFrame name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}
                                   rightActions={item.allowNavigation && <div className="pull-right">

                        <Button color="link" style={{ padding: 0 , fontSize: 'smaller' }} onClick={() => {


                        }}><i className="fa fa-map"/> Open Map</Button>

                    </div>}>

                    <Field name={name}>

                        {({ field , form: { setFieldValue } }) => {

                            return <AutoComplete {...field}
                                                 className={className}
                                                 disabled={isBusy}
                                                 style={{width: '100%'}}
                                                 types={['(regions)']}
                                                 placeholder={hint}
                                                 {...inputProps}
                                                 onChange={placeFull => setFieldValue(name,placeFull)}
                                                 onPlaceSelected={place => {

                                                     if(item.onChange)
                                                         item.onChange(place);
                                                 }}
                                                 componentRestrictions={{country: AppVariables.PlacesAutoCompleteCountry }}/>;
                        }}
                    </Field>


                </InputFrame>;

            case 'check':{

                let options = typeof items == "function" ? items() : items;
                const {  display , ...checkProps } = this.getFormContext;

                return <InputFrame showLabel={false}
                                   showComment={false}
                                   name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}>
                    <Field name={name}>
                        {({ field , form: { setFieldValue } }) => {

                            return <CheckGroup {...field}
                                               disabled={isBusy}
                                               options={options}
                                               display={display || "horizontal"}
                                               {...checkProps}
                                               onSelect={checked => {

                                                   setFieldValue(name,checked);
                                                   if(_.isFunction(item.onSelect))
                                                       item.onSelect(checked);

                                               }}/>
                        }}
                    </Field>
                </InputFrame>;
            }
            case 'radio':{

                const options = typeof item.items == "function" ? item.items() : item.items;
                return <InputFrame name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}>
                    <Field name={name}>
                        {({ field , form: { setFieldValue } }) =>
                            (<RadioGroup {...field}
                                         disabled={isBusy}
                                         {...inputProps}
                                         options={options}
                                         onSelect={value => {

                                            setFieldValue(name,value);

                                            if(_.isFunction(item.onSelect))
                                                item.onSelect(value);

                                         }}/>)
                        }
                    </Field>
                </InputFrame>;
            }
            case 'phone':{

                return <InputFrame  name={name}
                                    title={title}
                                    busyOverlay={busyInput && busyInput.overlay}
                                    comment={comment}
                                    isBusy={!!busyInput}
                                    inputStyle={inputStyle}
                                    icon={icon}>

                    <PhoneNumberInput name={name}
                                      disabled={isBusy}
                                      regionKey={`${name}-region`}
                                      includeRegion={true}
                                      inputClassName={className}
                                      placeholder={hint || item.placeholder}
                                      isRequired={required}/>

                </InputFrame>
            }
            case'toggle':{

                return <InputFrame name={name}
                                   showLabel={false}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}>
                    <Field name={name} >
                        {({ field , form: { setFieldValue , values } }) => {

                            return <ToggleInput
                                disabled={isBusy}
                                title={title}
                                hint={hint}
                                active={field.value}
                                style={inputStyle}
                                {...inputProps}
                                onChange={checked => {
                                    console.log('Changed: ',checked);
                                    setFieldValue(name,checked);
                                }}/>

                        }}
                    </Field>
                </InputFrame>;
            }
            case 'custom':{

                let content = <Field name={name}>
                    {params => (item.render(params))}
                </Field>;

                if(item.noFrame === true){
                    return content;
                }

                return <InputFrame name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}>
                    {content}
                </InputFrame>

            }
            default:

                return <InputFrame name={name}
                                   title={title}
                                   busyOverlay={busyInput && busyInput.overlay}
                                   comment={comment}
                                   isBusy={!!busyInput}
                                   inputStyle={inputStyle}
                                   icon={icon}>
                    <Field name={name}>
                        {({ field , form }) => {

                            return type === "textarea" ? <textarea
                                    {...field}
                                    className={className}
                                    disabled={isBusy}
                                    placeholder={hint}
                                    {...inputProps}
                                    onChange={evt => {

                                        if(_.isFunction(item.onChange)){
                                            item.onChange(evt.target.value);
                                        }

                                        field.onChange(evt);
                                    }}/> :

                                <input {...field}
                                       className={className}
                                       disabled={isBusy}
                                       placeholder={hint}
                                       {...inputProps}
                                       type={type}
                                       onChange={evt => {

                                           if(_.isFunction(item.onChange)){
                                               item.onChange(evt.target.value);
                                           }

                                           field.onChange(evt);
                                       }}/>
                        }}
                    </Field>
                </InputFrame>;
        }
    }

    removeInputError(key){

        const _context = this.getFormContext();
        let errors = _context.errors;
        if(errors.hasOwnProperty(key)){
            delete errors[key];
        }

        _context.setErrors(errors);

        this.setStateSafe(prevState => ({
            statusError:  Object.keys(errors).length > 0 ? prevState.statusError  : null
        }));

    }

    setInputError(key,msg){
        this.getFormContext().setFieldError(key,msg);
    }

    /**
     * Shows a progress indicator for the input
     * Note: Only supported by text like or select fields
     * @param name The name of the input
     * @param overlay Indicates whether to show overlay over all inputs
     */
    setInputBusy(name, overlay = false) {

        let busyInputs = this.state.busyInputs;
        busyInputs[name] = {
            overlay: overlay
        };

        this.setStateSafe({
            busyInputs: busyInputs
        });

    }

    cancelBusyInput(name){

        let busyInputs = this.state.busyInputs;
        if(busyInputs[name]) {
            delete busyInputs[name];
        }

        this.setStateSafe({
            busyInputs: busyInputs
        });
    }

    cancelBusyInputs(){

        this.setStateSafe({
            busyInputs: {}
        });

    }

    isInputBusy(name){
        return this.state.busyInputs[name];
    }

    async validate(){

        const  _context = this.getFormContext();
        let _faulted = await _context.validate();
        let keys = Object.keys(_faulted);
        let hasError = keys.length > 0;
        let statusError;
        if(hasError){

            const input = _faulted[keys[0]].input;

            if(input){
                Utils.flashInput(input);
            }

            statusError = keys.find((k) =>  _faulted[k].message ===  RequiredError ) ? "Please enter all required or invalid fields!" :  'Some fields were invalid. Resolve and try again!';
        }

        //  set new faulted input state
        this.setStateSafe({
            statusError: statusError
        });

        //  finally
        if(!hasError && this.props.onValidationPassed){
           await this.props.onValidationPassed(_context.values);
        }
        else if(hasError && this.props.onValidationFailed){
           await this.props.onValidationFailed(_faulted);
        }

        return !hasError;
    }

    getFormData(format = false){
        let values = this.getFormContext().values;
        return format ? this._formatValues(values, FormRenderer.getRealInputs(this.props.inputs)) : values;
    }

    bakeInputs(inputs,context){

        return inputs.map((input,i) => {

            let content;
            if(_.isFunction(input.render) && _.isNil(input.type)){
                content = (_.isArray(input.group) ? input.render(this.bakeInputs(input.group,context),context) : input.render({ context }) )
            }
            else{
                content = this.renderInput(input,context)
            }

            const wrapper = _.isFunction(input.container) ?  input.container : ((children,i) => <React.Fragment key={i}>{children}</React.Fragment>);
            return wrapper(content,i);
        });
    }

    clearErrors(){

        //  clear previous faulted inputs
        this.setStateSafe({
            statusError : null
        });

        //  clear form errors
        this.getFormContext().setErrors({});

    }

    /**
     * Get input value
     * @param name
     * @returns {*}
     */
    getInputValueWithName(name){
        return this.getFormContext().values[name];
    }

    /**
     * Set input value
     * @param name
     * @param value
     */
    setInputValueWithName(name,value){
        this.getFormContext().setFieldValue(name,value);
    }

    /**
     * Binds an item values to inputs
     */
    bindItem(item,clearError = false , transformValues = true){

        //  bind values
        this._form.resetForm(transformValues ? this._transformBindValues(item,FormRenderer.getRealInputs(this.props.inputs)) : item);

        //  clear error
        if(clearError)
            this.clearErrors();
    }

    /**
     * Finds the input with the given name
     * @param name
     * @returns {*}
     */
    findInput(name){
        return FormRenderer.getRealInputs(this.props.inputs).find(x => x.name === name);
    }

    /**
     * Refreshes the value of the input with the given bindItem
     * @param name The name of the input
     * @param bindItem The item containing the values to bind
     */
    refreshInput(name,bindItem){

        if(bindItem == null)
            return;

        //
        const input = this.findInput(name);
        const form = this.getFormContext();
        if(input){

            const { name , setValue } = input;
            let targetValue;
            if(_.isFunction(setValue)){
                setValue({

                    set value(value){
                        targetValue = value;
                    },

                    get value(){
                        return bindItem[name];
                    }
                }, bindItem);
            }
            else{
                targetValue = bindItem[name];
            }

            form.setFieldValue(name,targetValue);

        }

    }

    getFormContext(){
        return this._form.getFormikContext();
    }

    static getDerivedStateFromProps(props,currentState){

        if(_.isEqual(props.inputs,currentState.inputs)){

            return {
                inputs: props.inputs,
                initialValues: props.initialValues || FormRenderer.generateInitialValues(props.inputs),
                ...currentState.handleInputsChanged(props.inputs)
            };

        }

        return null;
    }

    static generateInitialValues(inputs) {

        let _state = {};
        FormRenderer.getRealInputs(inputs).map(x => {

            if (x.hasOwnProperty('defaultValue'))
                return _state[x.name] = x.defaultValue;

            let v;
            switch (x.type) {
                case "location":
                    v = null;
                    break;
                case "number":
                    v = 0;
                    break;
                case "toggle":
                    v = x.hasOwnProperty('active') ? x.active : false;
                    break;
                case "select": {

                        const _items = _.isFunction(x.items) ? x.items() : x.items;
                        if(Array.isArray(_items) && _items.length > 0){
                            v = _items[0].value;
                        }


                    }
                    break;
            }

            return _state[x.name] = (v === undefined ? "" : v);
        });

        return _state;
    }

    static isInputRequired(input){
        return (input.required === true || (input.hasOwnProperty('required') && input.required.hasOwnProperty('value') ? (input.required.value === true) : false ));
    }


    /**
     * Transforms values for form binding
     * @param values
     * @param inputs
     * @private
     */
    _transformBindValues(values,inputs){

        let _transformed = { };
        for(let i = 0, len = inputs.length; i < len; i++){

            let input = inputs[i];
            const { name , type , setValue } = input;

            if(_.isFunction(setValue)){

                setValue({

                    set value(value){
                        _transformed[name] = value;
                    },

                    get value(){
                        return values[name];
                    }

                },values);

                continue;
            }

            switch (type) {

                case 'location':{

                    _transformed[name] = {
                        text: values[name],
                        lat: values[ input.coord.lat],
                        lng: values[input.coord.lng]
                    };

                }break;

                case 'dropdown': {

                    if(input.simple !== true){
                        _transformed[name] = {
                            value: input.getItemValue(values[name]),
                            label: input.getItemLabel(values[name])
                        };
                    }

                }
                break;

                case "datetime":{

                    let _source = values[name];
                    _transformed[name] = _.isNil(_source) || _source === "" ? null : moment(_source);
                }
                break;

            }

            if(!_transformed.hasOwnProperty(name)){
                _transformed[name] = values[name];
            }


        }

        return _transformed;
    }

    /**
     * Formats values for submission
     * @param formData
     * @param inputs
     * @returns {*}
     * @private
     */
    _formatValues(formData,inputs){

        //  post input format
        if(!this.props.postInputFormat)
            return formData;

        const runFormatter = (input) => {

            //  set input name
            let _txf = input.format;

            switch (typeof _txf) {
                case "function":
                    break;
                case "string":
                    _txf = Formatters[_txf];
                    break;
            }

            return _txf ? _txf(formData[input.name]) : formData[input.name];
        };

        let _formatted = { };
        for(let i = 0 , len = inputs.length; i < len; i++){

            let input = inputs[i];
            switch (input.type) {

                case 'phone':{
                    _formatted[input.name] = Utils.fixPhone(runFormatter(input),formData[`${input.name}-region`]);
                }break;

                case 'location': {

                        let value = formData[input.name];
                        if (input.hasOwnProperty('coord')) {
                            _formatted[input.name] = value ? value.text : null;
                            _formatted[input.coord.lat] = value ? value.lat : null;
                            _formatted[input.coord.lng] = value ? value.lng : null;
                        }
                        else{
                            _formatted[input.name] = value ? value.text : null;
                        }

                        break;
                    }
                case 'dropdown':{

                    let _value = formData[input.name];
                    if(!_.isObject(_value) && _value && _value.hasOwnProperty('value') && _value.hasOwnProperty('label')){
                        _formatted[input.name] = _value.value;
                        break;
                    }

                }
                default: {
                    _formatted[input.name] = runFormatter(input);
                }break;

            }


        }

        return _formatted;
    }

    generateValidationSchema(inputs){

        let parts = { };
        const realInputs = FormRenderer.getRealInputs(inputs);
        for(let i = 0 , len = realInputs.length; i < len; i++){

            const input = realInputs[i];
            if(input.hasOwnProperty('validation')){
                parts[input.name] = input.validation;
                continue;
            }

            let val;
            switch(input.type){
                case "number":
                    val = Yup.number();
                    break;
                case "toggle":
                    val = Yup.bool();
                    break;
                case "phone": {

                    val = Yup.string();
                    if(FormRenderer.isInputRequired(input))
                        val = _.isBoolean(input.required) ? val.required() : val.required(input.required.message);

                    val = val.test("phone", "Invalid phone number", value => Utils.validatePhoneNumber(value, this.getFormData()[`${input.name}-region`]));

                }break;
                case "email":
                    val = Yup.string().email();
                    break;
                case "location":
                    val = Yup.mixed().nullable();
                    break;
                case "textarea":
                case "text":
                case "password":
                    val = Yup.string();
                    break;
                default:
                    val = Yup.mixed();
            }

            if(input.hasOwnProperty('sameAs')){
                val = val.oneOf([Yup.ref(input.sameAs.property),null],  input.sameAs.message || "Values do not match");
            }

            if(input.type !== "phone" && FormRenderer.isInputRequired(input)){
                val = _.isBoolean(input.required) ? val.required() : val.required(input.required.message);
            }

            if(input.type === 'email' && !FormRenderer.isInputRequired(input)){
                val = val.nullable().notRequired();
            }

            if(input.type === "location" && FormRenderer.isInputRequired(input)){
                val.when(input.name,{
                    is: value => !_.isNil(value) && typeof value === "object" ,
                    then: Yup.object().shape({
                        lat: Yup.number().nullable(),
                        lng: Yup.number().nullable(),
                        text: Yup.string().required().label(input.name)
                    })
                })
            }

            if(input.notRequired === true){
                val = val.notRequired();
            }

            if(input.hasOwnProperty('min')){
                val = val.min(input.min);
            }

            if(input.hasOwnProperty('max')){
                val = val.max(input.max);
            }

            //  Input title
            if(_.isString(input.title))
                val = val.label(input.title);

            parts[input.name] = val;
        }

        return  Yup.object().shape(parts);
    }

    render(){

        const { inputs , children } = this.props;
        const { initialValues , validationSchema } = this.state;

        return <Formik initialValues={initialValues}
                       enableReinitialize
                       ref={r => this._form = r}
                       validationSchema={validationSchema}
                       validate={async values => {

                           if(this.props.onValidate) {

                               let errors = await this.props.onValidate(values);
                               if(Object.keys(errors).length > 0)
                                   throw errors;

                           }

                       }}
                       onSubmit={async (values,{ setSubmitting }) => {

                           //   format values
                           let _formData = this._formatValues(values, FormRenderer.getRealInputs(inputs));

                           try{

                               if(_.isFunction(this.props.onSubmit)){
                                   await this.props.onSubmit(_formData);
                               }

                           }
                           catch (e) {

                               //   Failed submitting form

                           }

                           setSubmitting(false);
                       }}>
            {(form) => (<Form>
                    {this.bakeInputs(inputs,form)}
                    {this.props.showStatusError && this.state.statusError && <p className="error-label" style={{padding: '10px'}}>{this.state.statusError}</p>}
                    {_.isFunction(children) ? children(form) : children}
                </Form>)}
        </Formik>
    }
}

FormRenderer.propTypes = {
    inputs: PropTypes.any,
    inputStyle: PropTypes.object,
    busy: PropTypes.bool,
    onSubmit: PropTypes.func,
    onValidate: PropTypes.func,
    validationSchema: PropTypes.object,
    initialValues: PropTypes.object,
    getInputValue: PropTypes.func,
    setInputValue: PropTypes.func,
    onValidationPassed: PropTypes.func,
    onValidationFailed: PropTypes.func,
    showStatusError: PropTypes.bool,
    sessionKey: PropTypes.string,
    postInputFormat: PropTypes.bool
};

FormRenderer.defaultProps = {
    showStatusError: true,
    busy: false,
    postInputFormat: true
};

export default FormRenderer;


// WEBPACK FOOTER //
// ./src/common/FormRenderer/FormRenderer.js